import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DateTimePickerComponent } from '@syncfusion/ej2-react-calendars';
import type { MobilePlanRow } from '../../pages/mobile/mobileApi';
import { toLocalDateTimeString } from '../../utils/localDateTime';

export type RecordSubmit = {
  /** ISO datetime to set on the target operation. */
  whenIso: string;
  /** When true and a predecessor was offered, also end the predecessor at the same time. */
  alsoEndPredecessor: boolean;
};

type Props = {
  /** 'start' triggers actualStart on target; 'end' triggers actualEnd. */
  mode: 'start' | 'end';
  target: MobilePlanRow;
  /** Predecessor op that is currently IN_PROGRESS — only relevant in 'start' mode.
   *  When provided, the sheet shows a default-on checkbox to also set its actualEnd. */
  predecessor: MobilePlanRow | null;
  open: boolean;
  onClose: () => void;
  onSubmit: (submit: RecordSubmit) => Promise<void> | void;
};

/**
 * Local wall clock as `YYYY-MM-DDTHH:mm`, the shape the picker round-trips through
 * `new Date(manualWhen)`. Both the initial value and every pick have to use the local clock:
 * mixing it with `toISOString()` stored the UTC clock and the picker, which reads the string back
 * as local time, snapped the displayed value two hours back (review round 5, finding 1.3).
 */
function nowAsLocalIso(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  return toMinutePrecision(d);
}

function toMinutePrecision(date: Date): string {
  return toLocalDateTimeString(date).slice(0, 16);
}

export default function RecordTimeSheet({ mode, target, predecessor, open, onClose, onSubmit }: Props) {
  const { t } = useTranslation(['common']);

  const [useNow, setUseNow] = useState(true);
  const [manualWhen, setManualWhen] = useState<string>(nowAsLocalIso());
  const [alsoEndPredecessor, setAlsoEndPredecessor] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Whenever the sheet opens or target changes, reset internal state. Without
  // this, a previous 'other time' selection bleeds into the next interaction.
  useEffect(() => {
    if (open) {
      setUseNow(true);
      setManualWhen(nowAsLocalIso());
      setAlsoEndPredecessor(true);
      setSubmitting(false);
    }
  }, [open, target.executionUuid]);

  if (!open) {
    return null;
  }

  const title = mode === 'start' ? t('mobile.record.startTitle') : t('mobile.record.endTitle');
  const productLabel = target.productName ?? target.operationName ?? target.executionUuid;
  const orderLabel = target.orderNumber ? `#${target.orderNumber}` : '';

  async function handleSave() {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    // Local wall clock: the backend stores actualStart/actualEnd as LocalDateTime in the
    // server zone; toISOString() shifted every recording to the UTC clock (review round 5, 1.3).
    const whenIso = toLocalDateTimeString(useNow ? new Date() : new Date(manualWhen));
    try {
      await onSubmit({
        whenIso,
        alsoEndPredecessor: mode === 'start' && predecessor !== null && alsoEndPredecessor,
      });
    } finally {
      setSubmitting(false);
    }
  }

  // Ref-trick to bind onClick directly to the rendered DOM. Same pattern as
  // BatchCreation / NewTokenDialog: footer buttons inside Syncfusion Dialog
  // do not receive React onClick reliably.
  const footer = () => (
    <div className="flex gap-3 p-4 border-t">
      <ButtonComponent
        cssClass="flex-1"
        disabled={submitting}
        ref={(btn: any) => {
          if (btn && btn.element) {
            btn.element.onclick = () => onClose();
          }
        }}
      >
        {t('actions.cancel')}
      </ButtonComponent>
      <ButtonComponent
        cssClass="e-primary flex-1"
        disabled={submitting}
        ref={(btn: any) => {
          if (btn && btn.element) {
            btn.element.onclick = () => void handleSave();
          }
        }}
      >
        {submitting ? t('actions.saving') : t('actions.save')}
      </ButtonComponent>
    </div>
  );

  return (
    <DialogComponent
      visible={true}
      isModal={true}
      showCloseIcon={true}
      closeOnEscape={true}
      width="100%"
      position={{ X: 'center', Y: 'bottom' }}
      animationSettings={{ effect: 'SlideBottom' }}
      target="body"
      cssClass="record-time-sheet"
      zIndex={9000}
      header={title}
      close={onClose}
      footerTemplate={footer}
    >
      <div className="p-4 space-y-4">
        <div>
          <div className="text-base font-semibold">{productLabel}</div>
          {orderLabel && <div className="text-sm text-gray-500">{orderLabel}</div>}
        </div>

        <fieldset className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="recordWhen"
              checked={useNow}
              onChange={() => setUseNow(true)}
              className="w-5 h-5"
            />
            <span className="text-base">
              {t('mobile.record.now', {
                time: new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
              })}
            </span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="recordWhen"
              checked={!useNow}
              onChange={() => setUseNow(false)}
              className="w-5 h-5"
            />
            <span className="text-base">{t('mobile.record.otherTime')}</span>
          </label>

          {!useNow && (
            <div className="pl-8">
              <DateTimePickerComponent
                value={new Date(manualWhen)}
                change={(e: { value?: Date | null }) => {
                  if (e.value) {
                    setManualWhen(toMinutePrecision(e.value));
                  }
                }}
                format="dd.MM.yyyy HH:mm"
                step={5}
              />
            </div>
          )}
        </fieldset>

        {mode === 'start' && predecessor && (
          <label className="flex items-center gap-3 cursor-pointer pt-2 border-t">
            <input
              type="checkbox"
              checked={alsoEndPredecessor}
              onChange={(e) => setAlsoEndPredecessor(e.target.checked)}
              className="w-5 h-5"
            />
            <span className="text-sm">
              {t('mobile.record.alsoEndPrevious', {
                product: predecessor.productName ?? predecessor.operationName ?? '',
              })}
            </span>
          </label>
        )}
      </div>
    </DialogComponent>
  );
}
